import { HttpClient } from '@angular/common/http';
import { Component, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { select, Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import * as AppRootActions from '@app/app.actions';
import * as fromAppRoot from '@app/app.reducers';

import { CreatedPromotions, CreatedPromotionsError } from '@shared/models/Profile';
import { ToastNotificationService } from '@shared/services/toast-notification.service';
import { TrackingService } from '@shared/services/tracking.service';

@Component({
  selector: 'app-dialog-promotions',
  templateUrl: './dialog-promotions.component.html',
  styleUrls: ['./dialog-promotions.component.scss'],
  standalone: false
})
export class DialogPromotionsComponent implements OnDestroy {
  protected readonly onDestroy$ = new Subject<void>();
  success = false;
  promotionsCode = '';
  stepStatus: number;
  currentAccountStepStatus: number;
  idnowCompleted: boolean;
  issueLabel: string;
  kind: string;
  moneyPromotions: boolean;
  allMoneyPromotions = [];
  allFreePromotions = [];
  language: string;
  currency: string;
  isFirstTime = false;
  forReferring = false;
  referrerCode = '';
  activationDate: string;

  constructor(
    private store: Store,
    public http: HttpClient,
    private trackingService: TrackingService,
    private toastNotificationService: ToastNotificationService,
    private translateService: TranslateService
  ) {
    this.store.pipe(select(fromAppRoot.selectCustomerAndCurrentAccount), takeUntil(this.onDestroy$)).subscribe((value) => {
      if (!value || !value.customer || !value.customer.email) {
        return;
      }

      const higherStepAccount = value.customer.accounts.reduce(
        (a, b) => {
          return a.step_status > b.step_status ? a : b;
        },
        // initial value in case of empty array
        { step_status: 0 }
      );

      this.stepStatus = higherStepAccount.step_status;
      this.currentAccountStepStatus = value.currentAccountDetail.step_status;
      this.idnowCompleted = value.currentAccountDetail.customer_journey.id_verification_completed;
      this.kind = value.currentAccountDetail.kind;
      this.language = value.customer.language;
      this.currency = value.currentAccountDetail.currency;
    });

    this.store
      .pipe(select(fromAppRoot.selectPromotionsUpdateStatus), takeUntil(this.onDestroy$))
      .subscribe((response: CreatedPromotions | CreatedPromotionsError[]) => {
        const included = (response as CreatedPromotions).included;
        this.isFirstTime = included && !!included.length;

        if ('data' in response && response.data.type.length > 0) {
          this.trackingService.trackActivity(`Promotions: Success, code "${this.promotionsCode}" activated`, true);

          this.allMoneyPromotions = [];
          this.allFreePromotions = [];

          response.included.forEach((promo) => {
            if (promo['attributes']['type'] === 'MoneyPromotion') {
              this.moneyPromotions = true;
              this.allMoneyPromotions.push(promo);
            } else {
              this.allFreePromotions.push(promo);
            }
          });
          this.success = true;
        } else if ('errors' in response) {
          this.success = false;
          this.issueLabel = response['errors'][0]['meta']['code'];
          if (this.issueLabel === 'blank') {
            this.issueLabel = 'invalid';
          }
          this.trackingService.trackActivity(`Promotions: Error, code "${this.promotionsCode}" ${this.issueLabel}`, true);
        }
      });

    this.store.pipe(select(fromAppRoot.selectPromotions), takeUntil(this.onDestroy$)).subscribe((promotions) => {
      if (!promotions.included) {
        return;
      }

      this.allMoneyPromotions = [];
      this.allFreePromotions = [];

      promotions.included.forEach((promo) => {
        this.forReferring = promo.attributes.for_referring;
        if (this.forReferring) {
          // update referrer code
          this.referrerCode = promotions.meta.referrer_code;
        } else {
          // update activate date
          this.activationDate = promotions.data[0].attributes.valid_from;
        }

        if (promo.attributes.type === 'MoneyPromotion') {
          this.moneyPromotions = true;
          this.allMoneyPromotions.push(promo);
        } else {
          this.allFreePromotions.push(promo);
        }
      });

      const activePromotion = this.allMoneyPromotions.length ? this.allMoneyPromotions[0] : this.allFreePromotions[0];
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      this.promotionsCode = activePromotion ? activePromotion.attributes.code : '';
    });
  }

  submit(): void {
    this.issueLabel = '';

    const payload = {
      data: {
        type: 'promotion_redemptions',
        attributes: {
          code: this.promotionsCode
        }
      }
    };
    this.store.dispatch(AppRootActions.updatePromotions({ payload }));
  }

  advancedIssue(): boolean {
    const listOfAdvancedIssues = ['upperlimit', 'referrallimit', '3aMoneyError', 'moneyLimitError'];
    return listOfAdvancedIssues.includes(this.issueLabel);
  }

  close(): void {
    this.store.dispatch(AppRootActions.showPromotionsDialog({ show: false }));
  }

  ngOnDestroy(): void {
    this.store.dispatch(AppRootActions.resetPromotionsStatus());
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }
}
